import {RecipeCarouselV2, useSmartNavigate} from '@mfb/lego';
import * as React from 'react';
import {Element} from 'react-scroll';
import styled from 'styled-components';

import {GroupedPreferenceModel} from '../../customGraphQlModels/GroupedPreferenceModel';
import {ProductDbModel} from '../../customGraphQlModels/ProductDbModel';
import {mapRecipeCarouselV2Cta} from '../../contentful/recipeCarouselV2/utils/mapRecipeCarouselV2Cta';
import RecipeCarouselV2ContentModel from '../../contentful/recipeCarouselV2/RecipeCarouselV2ContentModel';
import {
  ALL_FILTER_ID,
  filterRecipesByPreference,
  getRecipeCarouselV2Filters,
} from '../../contentful/recipeCarouselV2/utils/getRecipeCarouselV2Filters';
import {getRecipeCarouselV2Weeks} from '../../contentful/recipeCarouselV2/utils/getRecipeCarouselV2Week';
import {
  CarouselV2Recipe,
  mapProductDetailsToCarouselV2Recipes,
  parseRecipeOrderValues,
} from '../../contentful/recipeCarouselV2/utils/mapRecipeCarouselV2';
import {mapRecipeDetail} from './mapRecipeDetail';
import RecipeDetailModal from './RecipeDetailModal';
import {useRecipeCarouselV2Data} from './useRecipeCarouselV2Data';

const SURCHARGE_DISCLAIMER =
  'Please note that certain recipes featured may be subject to a surcharge. Pricing information will be clearly displayed in your account area when selecting recipes.';

const StyledCarouselSpacer = styled.div`
  padding: ${(c) => c.theme.spacing.large} ${(c) => c.theme.spacing.large} 0;

  @media ${(c) => c.theme.layout.deviceMediaQueries.min.lg} {
    padding: 48px 48px 0;
  }
`;

export interface RecipeCarouselV2SectionProps {
  model: RecipeCarouselV2ContentModel;
  sku: string;
  product?: ProductDbModel;
  preferences?: Array<GroupedPreferenceModel>;
  preferenceCode?: string;
}

const RecipeCarouselV2Section: React.FunctionComponent<RecipeCarouselV2SectionProps> = ({
  model,
  sku,
  product,
  preferences = [],
  preferenceCode,
}) => {
  const {navigate} = useSmartNavigate();
  const weeks = React.useMemo(() => getRecipeCarouselV2Weeks(product), [product]);
  const [selectedWeekId, setSelectedWeekId] = React.useState(weeks[0]?.id);
  const week = weeks.find(w => w.id === selectedWeekId) ?? weeks[0];
  const canSwitchWeeks = (model.showDeliveryWeekSelector ?? false) && weeks.length > 1;

  const {data, isLoading, hasError} = useRecipeCarouselV2Data(product?.itemNumber ?? sku, week?.id);
  const [selectedFilterId, setSelectedFilterId] = React.useState(ALL_FILTER_ID);
  const [openRecipe, setOpenRecipe] = React.useState<CarouselV2Recipe>();
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    setSelectedWeekId(weeks[0]?.id);
  }, [weeks]);

  React.useEffect(() => {
    setSelectedFilterId(ALL_FILTER_ID);
  }, [data, preferenceCode]);

  const recipes = React.useMemo(() => {
    const mapped = mapProductDetailsToCarouselV2Recipes(data, {
      includeSurchargeRecipes: model.showSurchargeRecipes ?? false,
      orderValues: parseRecipeOrderValues(model.recipeOrderValues?.internal.content),
    });

    return preferenceCode ? filterRecipesByPreference(mapped, preferenceCode) : mapped;
  }, [data, model.showSurchargeRecipes, model.recipeOrderValues, preferenceCode]);

  const filters = React.useMemo(
    () => (model.showPreferenceSelector ? getRecipeCarouselV2Filters(preferences, product?.brand, recipes) : []),
    [model.showPreferenceSelector, preferences, product?.brand, recipes]
  );

  const modalModel = React.useMemo(() => openRecipe && mapRecipeDetail(openRecipe.source), [openRecipe]);

  if (!week || hasError || (!canSwitchWeeks && !isLoading && recipes.length === 0)) {
    return null;
  }

  const openRecipeDetail = (recipe: CarouselV2Recipe) => {
    setOpenRecipe(recipe);
    setIsModalOpen(true);
  };

  return (
    <Element name={model.id}>
      <StyledCarouselSpacer data-test="recipe-carousel-v2-section">
        <RecipeCarouselV2
          heading={model.heading}
          subHeading={model.subHeading}
          rowCount={model.rowsToDisplay ?? 1}
          recipes={filterRecipesByPreference(recipes, selectedFilterId)}
          weeks={canSwitchWeeks ? weeks : undefined}
          selectedWeekId={week.id}
          onWeekChange={selected => setSelectedWeekId(selected.id)}
          filters={filters}
          selectedFilterId={selectedFilterId}
          onFilterChange={filter => setSelectedFilterId(filter.id)}
          showBackground={model.showBackground ?? true}
          cta={mapRecipeCarouselV2Cta(model.callToAction, navigate)}
          disclaimer={model.showSurchargeRecipes ? SURCHARGE_DISCLAIMER : undefined}
          showNames={model.showRecipeNames ?? true}
          showBadges={model.showRecipeBadges ?? false}
          showMacros={model.showRecipeMacros ?? false}
          isLoading={isLoading}
          onRecipeClick={model.isRecipeCardClickable ? recipe => openRecipeDetail(recipe as CarouselV2Recipe) : undefined}
        />
        <RecipeDetailModal model={modalModel} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      </StyledCarouselSpacer>
    </Element>
  );
};

export default RecipeCarouselV2Section;
